import React, { useMemo } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import moment from 'moment'

import { constants } from 'helpers'
import { getTicketPriceString } from '../utils'

import { BlurredAvatars, HostProfileImage } from 'components'
import useEventData from '../hooks/useEventData'
import TicketButton from './TicketButton'
import EventTime from './EventTime'
import EventCountdown from './EventCountdown'
import MoreFromHostButton from './MoreFromHostButton'

import teleportLogo from 'assets/images/logo-small.png'

const {
  EVENT_CONSTANTS: {
    STATUS: { COMPLETED, ARCHIVED },
  },
} = constants

const JOIN_EVENT_STYLE = 'w-full flex justify-between items-center'

const newEventView = true

const JoinEventLabel = ({ ticketPrice, startTime, hostName, user, areUpcomingEvents }) => {
  const eventDate = useMemo(() => startTime && moment(startTime), [startTime])
  return (
    <div className="font-ownersWide text-2xl text-white tracking-tighter mr-3">
      {!newEventView && (
        <>
          {user?.id && !newEventView ? (
            areUpcomingEvents ? (
              'Join Event'
            ) : (
              'No Upcoming Events'
            )
          ) : (
            <EventCountdown startTime={startTime} hostName={hostName} eventDate={eventDate} />
          )}
        </>
      )}
      {!!ticketPrice && (
        <span className="font-ownersWide text-xl">{getTicketPriceString(ticketPrice)}</span>
      )}
    </div>
  )
}

const EventSeries = ({ event, childEvent, onReserve, history, user }) => {
  const eventData = useEventData({ event, childEvent })
  return (
    <div className="w-full flex justify-between items-center rounded-xl mb-2.5 bg-white bg-opacity-10 p-4">
      <div className="flex items-center">
        {event.host && (
          <HostProfileImage
            src={teleportLogo || event.host.logo}
            alt="company logo"
            className="w-8 h-8 rounded-full mr-2 md:mr-3.5"
          />
        )}
        <EventTime startDateString={eventData.startDateString} timeString={eventData.timeString} />
      </div>
      <div>
        <TicketButton
          event={event}
          eventData={eventData}
          onReserve={onReserve}
          history={history}
          user={user}
        />
      </div>
    </div>
  )
}

const TicketBox = ({ event, onReserve, user, history }) => {
  const areUpcomingEvents = useMemo(
    () => event.series.some(({ status }) => ![COMPLETED, ARCHIVED].includes(status)),
    [event]
  )
  const eventData = useEventData({ event, childEvent: event.series[0] })

  return (
    <div>
      <div className="uppercase mb-3 text-white text-2xl font-medium">
        {eventData.startDateString}, AT {eventData.timeString}
      </div>
      <div className={classnames(JOIN_EVENT_STYLE)}>
        <div className="w-full flex items-center">
          <JoinEventLabel
            ticketPrice={event.ticketPrice}
            startTime={event.startTime}
            hostName={event.host.name}
            user={user}
            areUpcomingEvents={areUpcomingEvents}
          />
          <BlurredAvatars />
        </div>
      </div>
      <div className="w-full h-auto flex flex-col justify-center items-center mt-5 lg:flex lg:mx-0">
        {user ? (
          areUpcomingEvents ? (
            event.series.map((childEvent, index) => (
              <EventSeries
                key={index}
                event={event}
                childEvent={childEvent}
                onReserve={onReserve}
                history={history}
                user={user}
              />
            ))
          ) : (
            <MoreFromHostButton
              linkId={event.host.linkId}
              hostName={event.host.name}
              history={history}
            />
          )
        ) : (
          <TicketButton
            className="w-full"
            event={event}
            eventData={{}}
            onClick={() => onReserve(event)}
          />
        )}
      </div>
    </div>
  )
}

TicketBox.propTypes = {
  onReserve: PropTypes.func,
  showAuthModal: PropTypes.func,
  user: PropTypes.object,
  event: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    status: PropTypes.string,
    location: PropTypes.string,
    image: PropTypes.string,
    ticketPrice: PropTypes.number,
    startTime: PropTypes.string,
  }),
}

export default TicketBox
